'use client'

import React from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import Link from 'next/link'

import { INPUT_TYPES } from '@/constants/utils'
import { passwordRecoverySentSchema } from '@/lib/schemas/guestAuth'
import { sendRecoveryLink } from '@/api/guest/recoveryPassword'
import ControllerField from '@/components/HookForm/components/ControllerField'
import Button from '@/components/livecontact/Button'
import EmptyState from '@/components/livecontact/EmptyState'
import LcIcon from '@/components/livecontact/icons'
import AuthHeading from '@/templates/SignInPage/components/AuthHeading'

/** Forgot password: email → "Send reset link" → inbox confirmation, in the sign-in card. */
const ForgotPasswordPage = () => {
  const { control, handleSubmit, getValues } = useForm({
    resolver: zodResolver(passwordRecoverySentSchema),
    defaultValues: { email: '' },
  })

  const mutationSendRecoveryLink = useMutation({
    mutationKey: ['sendRecoveryLink'],
    mutationFn: sendRecoveryLink,
  })

  if (mutationSendRecoveryLink.isSuccess) {
    return (
      <div className="w-full">
        <EmptyState
          icon={<LcIcon name="check" size={40} strokeWidth={2} className="text-success" />}
          title="Check your inbox"
          description={
            <>
              We sent a password reset link to{' '}
              <span className="break-all font-medium text-ink">{getValues('email')}</span>. It
              expires in a few hours.
            </>
          }
          action={
            <Button asChild variant="secondary" size="lg">
              <Link href="/sign-in">Back to sign in</Link>
            </Button>
          }
        />
        <p className="mt-6 text-center text-[12px] leading-[1.4] tracking-[-0.2px] text-ink-4">
          Didn’t get it? Check your spam folder or{' '}
          <button
            type="button"
            className="rounded-[4px] font-medium text-brand underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-brand/40"
            onClick={() => mutationSendRecoveryLink.reset()}
          >
            try another email
          </button>
          .
        </p>
      </div>
    )
  }

  return (
    <form
      className="w-full"
      onSubmit={handleSubmit((data) => mutationSendRecoveryLink.mutate(data))}
    >
      <AuthHeading
        backHref="/sign-in"
        title="Reset your password"
        description="Enter the email for your account and we’ll send you a link to choose a new password."
      />
      <ControllerField
        name="email"
        componentKey={INPUT_TYPES.FIELD}
        control={control}
        className="mb-5"
        classInput="h-10"
        label="Email"
        placeholder="you@business.com"
        type="email"
        autoFocus
        required
      />
      <Button
        type="submit"
        size="lg"
        className="w-full"
        loading={mutationSendRecoveryLink.isPending}
        trailingIcon={<LcIcon name="arrow-right" size={14} />}
      >
        Send reset link
      </Button>
    </form>
  )
}

export default ForgotPasswordPage
